/**
 * @module Experince/Product-Selector
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
// Core Libraries
import { Router } from '@angular/router';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';

// Services
import { ProductSelectorService } from '@experience/shop/product-selector/services/product-selector.service';
import { SharedBrowseService } from '@experience/shop/shared-browse/services/shared-browse.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';

// Interfaces
import { ProductSelectorConfigData } from '@core/aem/interfaces/product-selector/product-selector.interface';
import { BrowseEntry } from '@experience/shop/shared-browse/interfaces/browse-entry.interface';
// Constants
import { PRODUCT_SELECTOR_ROUTES } from '@experience/shop/product-selector/constants/product-selector.constants';
import { TileManagementService } from '@support/shared/services/tile-management.service';
import {
  LineDeivceDetails,
  lineTypeEnum,
} from '@experience/shop/product-selector/interfaces/product-selector.interface';
import { PRODUCT_OFFERINGS_CONSTANTS } from '@core/aem/components/product-offerings/product-offerings.constants';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { AemAuthorableData } from '@core/aem/interfaces/legal-text/aem-authorable.interface';
import { HARDGOODS_MODULE_COMPONENTS } from '@experience/shop/hardgoods/constants/hardgoods.constants';
import { EntryModalComponent } from '@experience/shop/shared-browse/components/entry-modal/entry-modal.component';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { PERMISSION_CONSTANTS } from '@support/shared/constants/permission.constants';
import { GlobalErrorService } from '@support/shared/widgets/services/global-error.service';
import { takeUntil } from 'rxjs/operators';
import { PdpDataHandlerService } from '@root/shared/services/pdp-data-handler.service';
import { FeatureToggleUtilService } from '@support/shared/services/feature-toggle-util.service';
import { PhoenixStateService } from '@root/shared/services/phoenix-state-service';
/**
 * Main Landing component for product selector
 */
@Component({
  selector: 'tmo-product-selector-landing',
  templateUrl: './product-selector-landing.component.html',
})
export class ProductSelectorLandingComponent extends PDLBaseComponent implements OnInit, OnDestroy {
  /**
   * Child component for EntryModal
   */
  @ViewChild('entryModal', { static: false }) public entryModal: EntryModalComponent;
  /** Selected values for browse entry */
  public selectedBrowseEntry: BrowseEntry;
  /** AEM data for the product selector page */
  public aemContent: ProductSelectorConfigData;

  /** Boolean to track spinner in product selector page */
  public showSpinner: boolean;
  public tileIDInFocusLineDetails: LineDeivceDetails;
  public componentName: string;
  /**
   * Holds whether focused line is existing or not
   */
  public isExistingLine: boolean;
  /**
   * Holds current non line item count
   */
  public nonLineItemCount: number;
  /**
   * Holds non line item count
   */
  public currentVoiceLineCount: number;
  public allLineDetails: LineDeivceDetails[];
  /** Unsubscriber for all subscribtions */
  private ngUnsubscribe: Subject<void>;
  /**
   * Boolean to check if max lines are reached
   */
  public maxLinesReached: boolean;
  /**
   * Boolean to check if user is in upgrade flow
   */
  public isUpgrade: boolean;
  /**
   * Boolean to check if flow is byos
   */
  public isByos: boolean;
  // public isRetail: boolean;
  /**
   * stores family name of the selected device
   */
  public productName: string;
  /**
   * checks if a product is selected
   */
  public productExists: boolean;
  /**
   * checks if page is product selector
   */
  public productSelectorLanding: boolean;

  /**
   * Stores value of legal text authorable filtered object
   */
  public legalText: AemAuthorableData[];
  /**
   * Stores value of byod sim data
   */
  public byodSimData: boolean;
  /**
   * Holds boolean disableAddALine
   */
  public disableAddALine: boolean;
  /**
   * Holds boolean to check if rep has permission to add a line.
   */
  public hasPermissionToAddALIne: boolean;
  /**
   * Holds boolean if user has test drive plan
   */
  public isTestDrivePlan: boolean;
  /**
   * checks if channel is Web
   */
  // private isWebChannel: boolean;
  public showConnect: boolean;

  public isNewProductSelectorTemplateEnabled: boolean;
  constructor(
    private router: Router,
    private stateStoreUtilsService: StateStoreUtilsService,
    private productSelectorService: ProductSelectorService,
    private tileManagementService: TileManagementService,
    private sharedBrowseService: SharedBrowseService,
    private globalErrorService: GlobalErrorService,
    private objectService: ObjectService,
    private cartSupportService: CartSupportService,
    public routeSnapshot: ActivatedRoute,
    public pdlService: PdlService,
    private pdpDataHandlerService: PdpDataHandlerService,
    private featureToggleUtilService: FeatureToggleUtilService,
    private phoenixStateService: PhoenixStateService
  ) {
    super(pdlService, routeSnapshot);
    this.ngUnsubscribe = new Subject<void>();
    this.maxLinesReached = false;
    this.productSelectorLanding = true;
    this.hasPermissionToAddALIne = this.cartSupportService.checkForAddALinePermission(
      PERMISSION_CONSTANTS.PRODUCT_SELECTOR,
      PERMISSION_CONSTANTS.ADD_A_LINE
    );
    this.isNewProductSelectorTemplateEnabled =
      this.featureToggleUtilService.isNewProductSelectorTemplateEnabled();
  }

  /**
   * Loads the initial configuration of Product Selector Page
   * @returns void
   */
  public ngOnInit(): void {
    // this.isWebChannel = SETTINGS.CHANNEL.toLowerCase() === CHANNELS.WEB.toLowerCase();
    this.initAEMData();
    this.tileIDInFocusLineDetails = this.tileManagementService.getFocusedTileIdDetails();
    this.nonLineItemCount = this.stateStoreUtilsService.getTileIdList(lineTypeEnum.NONLINE).length;
    this.currentVoiceLineCount = this.stateStoreUtilsService.getTileIdList(
      lineTypeEnum.LINE
    ).length;
    this.componentName = PRODUCT_OFFERINGS_CONSTANTS.COMPONENT_NAME;
    this.tileIDInFocusLineDetails = this.tileManagementService.getFocusedTileIdDetails();
    // setting tileIDInFocusLineDetails from persisted state if found
    this.phoenixStateService.get('StateService', 'cart.lineDetails').subscribe((res) => {
      if (res) {
        this.tileIDInFocusLineDetails = res;
        this.stateStoreUtilsService.setFocusedTileLineNonLineItem(res,res.tileId)
      }
    })
    this.getAllLineDetails();
    this.getUpdatedFocusedTileDetails();
    this.disableAddCTA();
    this.isUpgrade = this.stateStoreUtilsService.getUpgradeFlow();
    this.showConnect = document.cookie.includes('brand=connect') ? true : false;
    // QC613477
    this.isByos =
      this.cartSupportService.isByosFlow(); /* && !(SETTINGS.CHANNEL.toLowerCase() === CHANNELS.RETAIL);*/
    this.productName = this.objectService.get(
      this.tileManagementService.getFocusedTileIdDetails(),
      'deviceDetails.familyName'
    );
    this.byodSimData =
      this.objectService.get(
        this.tileManagementService.getFocusedTileIdDetails(),
        'byodSimData',
        false
      ) && !this.cartSupportService.isByosFlow();
    this.productExists =
      this.objectService.get(
        this.tileManagementService.getFocusedTileIdDetails(),
        'deviceDetails',
        false
      ) || this.byodSimData;
  }

  /**
   * Destroys all the subscriptions
   * @returns {void}
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * method to clear global error messages on child route change
   * @returns void
   */
  public onActivate(): void {
    this.globalErrorService.clearGlobalErrorMessage();
  }
  /**
   * function gotoCart for Lines
   * @method goToCart()
   * @returns void
   */
  public goToCart(): void {
    let params;
    const isUpgrade = this.objectService.get(this.tileIDInFocusLineDetails, 'isUpgrade');
    if (isUpgrade) {
      params = { queryParams: { isUpgrade: true } };
      this.router.navigate([PRODUCT_SELECTOR_ROUTES.CART], params);
    } else {
      this.router.navigate([PRODUCT_SELECTOR_ROUTES.CART]);
    }
  }

  /**
   * Function calls add line in productSelector Service
   * @method saveAndAddLine
   * @param canAddLine boolean indicates if we can add line based on maxLimit
   * @returns {void}
   */
  public saveAndAddLine(canAddLine?: boolean): void {
    if (this.maxLinesReached) {
      this.tileManagementService.createTile(lineTypeEnum.NONLINE, true);
    } else {
      this.tileManagementService.createTile();
    }
    this.tileManagementService.getUpdatedData(true);
    const tileId = this.stateStoreUtilsService.getFocusedTileId();
    this.router.navigate([PRODUCT_OFFERINGS_CONSTANTS.NAVIGATE_TO_PRODUCT_SELECTOR, tileId]);
  }

  /**
   * Routes to the product selector page on click of the continue button
   * @param event : Holds the value of the Event
   * @returns void
   */
  public closeModal(event: Event): void {
    this.selectedBrowseEntry = this.sharedBrowseService.getUserInputsConfigData();
  }
  /**
   * Function used to get focused tile id details
   */
  private getUpdatedFocusedTileDetails(): void {
    this.tileManagementService.focusedTileDetails
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((data: LineDeivceDetails) => {
        this.tileIDInFocusLineDetails = data;
        this.productName = this.objectService.get(
          this.tileIDInFocusLineDetails,
          'deviceDetails.familyName'
        );
        this.byodSimData =
          this.objectService.get(this.tileIDInFocusLineDetails, 'byodSimData', false) &&
          !this.cartSupportService.isByosFlow();
        this.productExists =
          this.objectService.get(this.tileIDInFocusLineDetails, 'deviceDetails', false) ||
          this.byodSimData;
        this.isExistingLine =
          this.objectService.get(data, 'lineTileType') === lineTypeEnum.EXISTING;
        this.maxLinesReached = this.cartSupportService.isMaxLineReached();
        this.disableAddCTA();
      });
  }

  /**
   * Function used to get all line, non-line, existing line details.
   */
  private getAllLineDetails(): void {
    this.tileManagementService.getLineNonLineItemDetails
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((data: LineDeivceDetails[]) => {
        this.allLineDetails = data;
        this.nonLineItemCount = this.stateStoreUtilsService.getTileIdList(
          lineTypeEnum.NONLINE
        ).length;
        this.currentVoiceLineCount = this.stateStoreUtilsService.getTileIdList(
          lineTypeEnum.LINE
        ).length;
      });
  }

  /**
   * Retrieves AEM content for product selector landing component
   * @returns {void}
   */
  private initAEMData(): void {
    this.productSelectorService
      .getProductSelectorContent()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data: ProductSelectorConfigData) => {
        this.showSpinner = false;
        this.aemContent = this.objectService.get(data, 'configurableData');
        const authorableData = this.objectService.get(data, 'authorableData');
        this.legalText = authorableData.filter(
          (aemData: AemAuthorableData) =>
            aemData.uiComponent === HARDGOODS_MODULE_COMPONENTS.LEGAL_TEXT
        );
        // if (this.isWebChannel) {
        const cart = this.stateStoreUtilsService.getCart();
        const isCartEmpty = !this.cartSupportService.isCartNotEmpty(cart);
        this.pdpDataHandlerService.updateCartForPDL(cart, isCartEmpty);
        this.pdlService.triggerCustomEvent(this.pdlPageComponentId, 'pageLoad');
        // }
      });
  }

  /**
   * Function triggers update of lineId in focus to last line in cart
   */
  public disableAddCTA(): void {
    this.isTestDrivePlan = this.cartSupportService.checkTestDrivePlanInCart();
    const isByos = this.cartSupportService.isByosFlow();
    // this.disableAddALine = (this.maxLinesReached && isByos &&
    //   SETTINGS.CHANNEL.toLowerCase() !== CHANNELS.RETAIL && this.objectService.get(this.tileIDInFocusLineDetails,
    //   'isLineSetupCompleted')) ? ( isByos ) :
    //   !this.objectService.get(this.tileIDInFocusLineDetails, 'isLineSetupCompleted');
    this.disableAddALine =
      this.maxLinesReached &&
      isByos &&
      this.objectService.get(this.tileIDInFocusLineDetails, 'isLineSetupCompleted')
        ? isByos
        : !this.objectService.get(this.tileIDInFocusLineDetails, 'isLineSetupCompleted');
  }

  /**
   * Output event of tmo-container-holder component
   */
  public hideSpinner(hide: Boolean): void {}

  /**
   * Navigate to Landing page
   */
  public navigateHome(): void {
    const route = this.stateStoreUtilsService.getCustomerId()
      ? PRODUCT_SELECTOR_ROUTES.ACCOUNT_LANDING
      : this.cartSupportService.getHomePage();
    if (route.match(new RegExp(PRODUCT_SELECTOR_ROUTES.HTTP_URL, 'i'))) {
      window.location.href = route;
    } else {
      this.router.navigate([route]);
    }
  }
}
